import React, { useEffect, useRef } from "react";
import { TableProps } from "../../../interfaces/table";
import { useInvoiceStore } from "../../../stores/invoice";
import DeleteModal from "../modals/DeleteModal";
import useModals from "../../../hooks/useModals";
import { getInvoicePdf, getProformaPdf } from "../../../api/invoices";
import { showToastError } from "../../../services/toast";
import { downloadCreditNote } from "../../../api/creditNotes";
import { useUtilsStore } from "../../../stores/components/utils";
import {
  Checkbox,
  DesktopTable,
  DownloadIcon,
  EyeIcon,
  IconButton,
  LinkedRowSummary,
  MicroLabel,
  MobileCardFooter,
  MobileCardList,
  MobileField,
  MobileFieldGrid,
  TableEmptyState,
  TableFrame,
  Td,
  Th,
  TrashIcon,
  cardClasses,
  getNumericAccessors,
  renderCellValue,
  rowClasses,
} from "./tableUi";

const TableInvoices = ({ columns, data }: TableProps) => {
  // Store
  const {
    idsProformasSelected,
    setIdsProformasSelected,
    setViewOrUpdate,
    setIdProformaSelected,
    tabInvoice,
  } = useInvoiceStore();

  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  const { searchTerm } = useUtilsStore();

  // Ajout de la ref pour le conteneur du tableau
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Fonction pour gérer le clic sur une ligne
  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement | HTMLDivElement>,
    id: number,
  ) => {
    e.stopPropagation();
    e.preventDefault();

    if (tabInvoice === "Avoirs" || tabInvoice === "Proformas") {
      setIdProformaSelected(id);
      setViewOrUpdate("update");
    } else {
      setViewOrUpdate("update");
    }
  };

  // Fonction pour gérer la checkbox sur une ligne
  const handleRowClickCheck = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number,
  ) => {
    e.stopPropagation();
    if (e.target.checked) {
      setIdsProformasSelected((prev) => [...prev, id]);
    } else {
      setIdsProformasSelected((prev) =>
        prev.filter((invoice) => invoice !== id),
      );
    }
  };

  const handleRowClickAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setIdsProformasSelected(
        data
          .filter((invoice) => invoice.noBillingContacts === false)
          .map((invoice) => invoice.id),
      );
    } else {
      setIdsProformasSelected([]);
    }
  };

  const handleViewInvoice = (id: number) => {
    getProformaPdf(id)
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        );

        // Ouvrir le PDF dans une nouvelle fenêtre
        window.open(url, "_blank");
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du fichier");
      });
  };

  const handleViewCreditNotes = (id: number) => {
    downloadCreditNote(id)
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        );

        // Ouvrir le PDF dans une nouvelle fenêtre
        window.open(url, "_blank");
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du fichier");
      });
  };

  const handleDownloadInvoice = (id: number, name: string) => {
    const downloadPdf = (getPdfFunction: Function, fileNamePrefix: string) => {
      getPdfFunction(id)
        .then((response: any) => {
          const url = window.URL.createObjectURL(
            new Blob([response.data], { type: "application/pdf" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${fileNamePrefix}_${name}`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          showToastError("Erreur lors du téléchargement du fichier");
        });
    };

    if (tabInvoice === "Proformas") {
      downloadPdf(getProformaPdf, "proformas");
    } else if (tabInvoice === "Factures") {
      downloadPdf(getInvoicePdf, "factures");
    }
  };

  const handleDownloadCreditNote = (id: number, name: string) => {
    downloadCreditNote(id)
      .then((response: any) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `avoir_${name}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du fichier");
      });
  };

  useEffect(() => {
    if (searchTerm !== "" && tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  const numericAccessors = getNumericAccessors(columns, data);
  const [primaryColumn, ...secondaryColumns] = columns;

  const allSelectableChecked =
    idsProformasSelected.length > 0 &&
    idsProformasSelected.length ===
      data.filter((invoice) => invoice.noBillingContacts === false).length;

  const isRowSelectable = (row: Record<string, any>) =>
    ((tabInvoice === "Proformas" || tabInvoice === "Avoirs") &&
      row.noBillingContacts === false) ||
    tabInvoice === "Factures";

  // Actions d'une ligne, partagées entre la table et les cartes
  const renderRowActions = (row: Record<string, any>) => (
    <div className="flex items-center gap-1.5">
      <IconButton
        label="Visualiser"
        tone="cyan"
        onClick={(e) => {
          e.stopPropagation();
          if (tabInvoice === "Avoirs") {
            handleViewCreditNotes(row.id);
          } else {
            handleViewInvoice(row.id);
          }
        }}
      >
        <EyeIcon />
      </IconButton>
      <IconButton
        label="Télécharger"
        tone="blue"
        onClick={(e) => {
          e.stopPropagation();
          if (tabInvoice === "Avoirs") {
            handleDownloadCreditNote(row.id, row.invoiceNumber);
          } else {
            handleDownloadInvoice(row.id, row.invoiceNumber);
          }
        }}
      >
        <DownloadIcon />
      </IconButton>
      {tabInvoice !== "Factures" && (
        <IconButton
          label="Supprimer"
          tone="danger"
          onClick={(e) => {
            e.stopPropagation();
            openModal(
              "deleteModal",
              <DeleteModal
                closeModal={() => closeModal("deleteModal")}
                id={row.id}
              />,
            );
          }}
        >
          <TrashIcon />
        </IconButton>
      )}
    </div>
  );

  const renderCreditNoteActions = (creditNote: any) => (
    <div className="flex items-center gap-1.5">
      <IconButton
        label="Visualiser l'avoir"
        tone="cyan"
        onClick={(e) => {
          e.stopPropagation();
          handleViewCreditNotes(creditNote.id);
        }}
      >
        <EyeIcon />
      </IconButton>
      <IconButton
        label="Télécharger l'avoir"
        tone="blue"
        onClick={(e) => {
          e.stopPropagation();
          handleDownloadCreditNote(creditNote.id, creditNote.creditNoteNumber);
        }}
      >
        <DownloadIcon />
      </IconButton>
    </div>
  );

  const renderCreditNoteSummary = (creditNote: any) => (
    <LinkedRowSummary
      label="Avoir"
      reference={`#${creditNote.creditNoteNumber}`}
      amountLabel="Montant TTC"
      amount={`${creditNote.totalAmount}€`}
    />
  );

  const emptyTitle =
    tabInvoice === "Factures"
      ? "Aucune facture trouvée"
      : tabInvoice === "Avoirs"
        ? "Aucun avoir trouvé"
        : "Aucune proforma trouvée";

  const emptyDescription =
    tabInvoice === "Factures"
      ? "Les factures apparaîtront ici une fois créées."
      : tabInvoice === "Avoirs"
        ? "Les avoirs apparaîtront ici une fois créés."
        : "Les proformas apparaîtront ici une fois créées.";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}

      <TableFrame scrollRef={tableContainerRef}>
        {data.length === 0 ? (
          <TableEmptyState title={emptyTitle} description={emptyDescription} />
        ) : (
          <>
            {/* Vue tableau — écrans larges */}
            <DesktopTable>
              <thead className="sticky top-0 z-10">
                <tr>
                  <Th className="w-px">
                    <Checkbox
                      label="Tout sélectionner"
                      checked={allSelectableChecked}
                      onChange={(e) => handleRowClickAll(e)}
                    />
                  </Th>
                  {columns.map((col, index) => (
                    <Th
                      key={index}
                      align={numericAccessors.has(col.accessor) ? "right" : "left"}
                      width={col.header === "Dossier" ? "20%" : undefined}
                    >
                      {col.header}
                    </Th>
                  ))}
                  <Th align="right" className="w-px">
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody className="font-Nunito">
                {data.map((row, rowIndex) => {
                  const hasCreditNotes = row?.creditNotes?.length > 0;
                  const isSelected = idsProformasSelected.includes(row.id);

                  return (
                    <React.Fragment key={rowIndex}>
                      {/* Ligne de la facture principale */}
                      <tr
                        className={rowClasses({
                          highlighted: hasCreditNotes,
                          selected: isSelected,
                          clickable: tabInvoice !== "Factures",
                        })}
                        onClick={(e) =>
                          tabInvoice === "Factures"
                            ? null
                            : handleRowClick(e, row.id)
                        }
                      >
                        <Td>
                          {isRowSelectable(row) ? (
                            <Checkbox
                              label="Sélectionner la ligne"
                              checked={isSelected}
                              onChange={(e) => handleRowClickCheck(e, row.id)}
                            />
                          ) : null}
                        </Td>
                        {columns.map((col, colIndex) => (
                          <Td
                            key={colIndex}
                            align={
                              numericAccessors.has(col.accessor)
                                ? "right"
                                : "left"
                            }
                            className={
                              col.header === "Dossier"
                                ? "font-semibold text-slate-900"
                                : "text-slate-600"
                            }
                          >
                            {renderCellValue(row[col.accessor])}
                          </Td>
                        ))}

                        <Td>
                          <div className="flex justify-end">
                            {renderRowActions(row)}
                          </div>
                        </Td>
                      </tr>

                      {/* Lignes des avoirs liés */}
                      {row?.creditNotes?.map(
                        (creditNote: any, creditIndex: any) => (
                          <tr
                            key={`${rowIndex}-credit-${creditIndex}`}
                            className="bg-amber-50/40"
                          >
                            <Td dense />
                            <Td colSpan={columns.length} dense>
                              {renderCreditNoteSummary(creditNote)}
                            </Td>
                            <Td dense>
                              <div className="flex justify-end">
                                {renderCreditNoteActions(creditNote)}
                              </div>
                            </Td>
                          </tr>
                        ),
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </DesktopTable>

            {/* Vue cartes — mobile et tablette */}
            <MobileCardList>
              {data.map((row, rowIndex) => {
                const hasCreditNotes = row?.creditNotes?.length > 0;
                const isSelected = idsProformasSelected.includes(row.id);

                return (
                  <li key={rowIndex}>
                    <div
                      onClick={(e) =>
                        tabInvoice === "Factures"
                          ? null
                          : handleRowClick(e, row.id)
                      }
                      className={cardClasses({
                        highlighted: hasCreditNotes,
                        selected: isSelected,
                      })}
                    >
                      <div className="flex items-start gap-3">
                        {isRowSelectable(row) ? (
                          <span className="pt-1">
                            <Checkbox
                              label="Sélectionner la ligne"
                              checked={isSelected}
                              onChange={(e) => handleRowClickCheck(e, row.id)}
                            />
                          </span>
                        ) : null}
                        <div className="min-w-0">
                          <MicroLabel>{primaryColumn?.header}</MicroLabel>
                          <p className="mt-0.5 text-[15px] leading-snug font-bold wrap-break-word text-slate-900">
                            {primaryColumn
                              ? renderCellValue(row[primaryColumn.accessor])
                              : null}
                          </p>
                        </div>
                      </div>

                      {secondaryColumns.length > 0 && (
                        <MobileFieldGrid>
                          {secondaryColumns.map((col) => (
                            <MobileField
                              key={col.accessor}
                              label={col.header}
                              value={renderCellValue(row[col.accessor])}
                              numeric={numericAccessors.has(col.accessor)}
                            />
                          ))}
                        </MobileFieldGrid>
                      )}

                      <MobileCardFooter>
                        {renderRowActions(row)}
                      </MobileCardFooter>
                    </div>

                    {/* Avoirs liés */}
                    {row?.creditNotes?.map(
                      (creditNote: any, creditIndex: any) => (
                        <div
                          key={`${rowIndex}-credit-${creditIndex}`}
                          className="mt-1.5 ml-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3"
                        >
                          {renderCreditNoteSummary(creditNote)}
                          {renderCreditNoteActions(creditNote)}
                        </div>
                      ),
                    )}
                  </li>
                );
              })}
            </MobileCardList>
          </>
        )}
      </TableFrame>
    </div>
  );
};

export default TableInvoices;
